import {
  Text,
  Flex,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  FlexProps,
  useDisclosure,
} from "@chakra-ui/react";
import ScanIcon from "../icons/ScanIcon";
import Card from "../Card";
import BoxIcon from "../icons/BoxIcon";
import {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { CommandStatus } from "../../types/command.types";
import ChangeStatusModal from "./ChangeStatusModal";
import CheckIcon from "../icons/CheckIcon";
import PrepCommandModal from "./PrepCommandModal";

type ScanModalProps = {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
};

enum ScanModalStatus {
  NONE,
  CHANGE_STATUS,
  CONFIRM_COMMAND,
  PREP_ORDER,
}

type ScanModalChangeStatusBodyProps = {
  defaultStatus?: CommandStatus | null;
  onUpdate?: (status: CommandStatus | null) => void;
};
type ScanModalChangeStatusBodyHandle = {
  status: () => CommandStatus | null;
};

const ScanModalChangeStatusBody = forwardRef<
  ScanModalChangeStatusBodyHandle,
  ScanModalChangeStatusBodyProps
>(({ defaultStatus, onUpdate }, ref) => {
  const [commandStatus, setCommandStatus] = useState<CommandStatus | null>(
    defaultStatus ?? null
  );

  useImperativeHandle(ref, () => ({
    status: () => {
      return commandStatus;
    },
  }));

  const SmallButton = ({
    children,
    onClick,
    isActive,
  }: FlexProps & { isActive: boolean }) => {
    return (
      <Flex
        onClick={onClick}
        color={isActive ? "white" : "gray.700"}
        bg={isActive ? "brand.700" : "white"}
        border="solid 1px"
        borderColor={isActive ? "brand.700" : "gray.300"}
        borderRadius="lg"
        px={3}
        py={1}
        transition="background-color 0.5s, border-color 0.5s"
      >
        {children}
      </Flex>
    );
  };

  const handleUpdateCommandStatus = useCallback(
    (status: CommandStatus | null) => {
      setCommandStatus(status);
      if (onUpdate) {
        onUpdate(status);
      }
    },
    [onUpdate]
  );

  return (
    <Flex>
      <Flex flexWrap={"wrap"} gap={2} my={3}>
        <SmallButton
          onClick={() =>
            handleUpdateCommandStatus(CommandStatus.WAITING_FOR_RECEPTION)
          }
          isActive={commandStatus === CommandStatus.WAITING_FOR_RECEPTION}
        >
          Attente de réception
        </SmallButton>
        <SmallButton
          onClick={() => handleUpdateCommandStatus(CommandStatus.RECEIVED)}
          isActive={commandStatus === CommandStatus.RECEIVED}
        >
          Receptionné
        </SmallButton>
        <SmallButton
          onClick={() => handleUpdateCommandStatus(CommandStatus.MODELING)}
          isActive={commandStatus === CommandStatus.MODELING}
        >
          En modélisation
        </SmallButton>
        <SmallButton
          onClick={() => handleUpdateCommandStatus(CommandStatus.PRINTING)}
          isActive={commandStatus === CommandStatus.PRINTING}
        >
          En impression
        </SmallButton>
        <SmallButton
          onClick={() => handleUpdateCommandStatus(CommandStatus.INJECTING)}
          isActive={commandStatus === CommandStatus.INJECTING}
        >
          En injection
        </SmallButton>
        <SmallButton
          onClick={() => handleUpdateCommandStatus(CommandStatus.POLISHING)}
          isActive={commandStatus === CommandStatus.POLISHING}
        >
          En finition
        </SmallButton>
        <SmallButton
          onClick={() => handleUpdateCommandStatus(CommandStatus.QUALITY_CONTROL)}
          isActive={commandStatus === CommandStatus.QUALITY_CONTROL}
        >
          En contrôle qualité
        </SmallButton>
        <SmallButton
          onClick={() =>
            handleUpdateCommandStatus(CommandStatus.READY_FOR_DELIVERY)
          }
          isActive={commandStatus === CommandStatus.READY_FOR_DELIVERY}
        >
          Prêt à livrer
        </SmallButton>
        <SmallButton
          onClick={() => handleUpdateCommandStatus(CommandStatus.DELIVERED)}
          isActive={commandStatus === CommandStatus.DELIVERED}
        >
          Terminé
        </SmallButton>
        <SmallButton
          onClick={() => handleUpdateCommandStatus(CommandStatus.ABORTED)}
          isActive={commandStatus === CommandStatus.ABORTED}
        >
          Annulé
        </SmallButton>
        <SmallButton
          onClick={() => handleUpdateCommandStatus(CommandStatus.PENDING)}
          isActive={commandStatus === CommandStatus.PENDING}
        >
          En pause
        </SmallButton>
      </Flex>
    </Flex>
  );
});

type ShippingType = "UPS" | "OTHER";

type ScanModalPrepOrderBodyProps = {
  defaultStatus?: ShippingType | null;
  onUpdate?: (status: ShippingType | null) => void;
};
type ScanModalPrepOrderBodyHandle = {
  status: () => ShippingType | null;
};

const ScanModalPrepOrderBody = forwardRef<
  ScanModalPrepOrderBodyHandle,
  ScanModalPrepOrderBodyProps
>(({ defaultStatus, onUpdate }, ref) => {
  const [shippingType, setShippingType] = useState<ShippingType | null>(
    defaultStatus ?? null
  );

  useImperativeHandle(ref, () => ({
    status: () => {
      return shippingType;
    },
  }));

  const SmallButton = ({
    children,
    onClick,
    isActive,
  }: FlexProps & { isActive: boolean }) => {
    return (
      <Flex
        onClick={onClick}
        color={isActive ? "white" : "gray.700"}
        bg={isActive ? "brand.700" : "white"}
        border="solid 1px"
        borderColor={isActive ? "brand.700" : "gray.300"}
        borderRadius="lg"
        px={3}
        py={1}
        transition="background-color 0.5s, border-color 0.5s"
      >
        {children}
      </Flex>
    );
  };

  const handleUpdateCommandStatus = useCallback(
    (status: ShippingType | null) => {
      setShippingType(status);
      if (onUpdate) {
        onUpdate(status);
      }
    },
    [onUpdate]
  );

  return (
    <Flex mt={4}>
      <Flex
        flexWrap={"wrap"}
        gap={2}
        my={3}
        alignItems="center"
        w="100%"
        justify="center"
      >
        <SmallButton
          onClick={() => handleUpdateCommandStatus("UPS")}
          isActive={shippingType === "UPS"}
        >
          Avec UPS
        </SmallButton>
        <SmallButton
          onClick={() => handleUpdateCommandStatus("OTHER")}
          isActive={shippingType === "OTHER"}
        >
          Sans UPS
        </SmallButton>
      </Flex>
    </Flex>
  );
});

export type ScanModalHandle = {
  reset: () => void;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ScanModal = forwardRef<ScanModalHandle, ScanModalProps>(
  ({ isOpen, onOpen, onClose }, ref) => {
    const [actionStatus, setActionStatus] = useState(ScanModalStatus.NONE);
    const [changeToStatus, setChangeToStatus] = useState<CommandStatus | null>(
      null
    );
    const [prepCommandShippingType, setPrepCommandShippingType] =
      useState<ShippingType | null>(null);
    const {
      isOpen: changeStatusIsOpen,
      onOpen: changeStatusOnOpen,
      onClose: changeStatusOnClose,
    } = useDisclosure();
    const prepCommandModal = useDisclosure();

    const handleChangeState = useCallback(
      (status: ScanModalStatus) => {
        if (status !== actionStatus) {
          setChangeToStatus(null);
        }
        setActionStatus(status);
      },
      [actionStatus]
    );

    useImperativeHandle(ref, () => ({
      reset: () => {
        handleChangeState(ScanModalStatus.NONE);
      },
    }));

    const canGoNext = useMemo(() => {
      // 1. Should be on one of valid status
      if (
        ![ScanModalStatus.CHANGE_STATUS, ScanModalStatus.PREP_ORDER].includes(
          actionStatus
        )
      ) {
        return false;
      }
      // 2. Validation per status
      switch (actionStatus) {
        case ScanModalStatus.NONE:
          return false;
        case ScanModalStatus.CHANGE_STATUS:
          return changeToStatus !== null;
        case ScanModalStatus.PREP_ORDER:
          return prepCommandShippingType !== null;
      }
      // 3. Default is to not allow confirmation
      return false;
    }, [actionStatus, changeToStatus, prepCommandShippingType]);

    const handleGoNext = useCallback(() => {
      switch (actionStatus) {
        case ScanModalStatus.CHANGE_STATUS: {
          if (canGoNext) {
            onClose();
            changeStatusOnOpen();
          }
          break;
        }
        case ScanModalStatus.PREP_ORDER: {
          if (canGoNext) {
            onClose();
            prepCommandModal.onOpen();
          }
          break;
        }
        default:
          return;
      }
    }, [actionStatus, canGoNext, onClose, changeStatusOnOpen]);

    return (
      <>
        <Modal isOpen={isOpen} onClose={() => {}} isCentered>
          <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(6.9px)" />
          <ModalContent maxW={"none"} w="1000px">
            <ModalHeader>
              <Flex>
                <Flex
                  w="48px"
                  h="48px"
                  bg="zinc.50"
                  border="solid 1px"
                  borderColor="brand.100"
                  justifyContent="center"
                  alignItems="center"
                  borderRadius={100}
                  mr="8px"
                >
                  <ScanIcon w="24px" h="24px" color="brand.700"></ScanIcon>
                </Flex>
                <Text lineHeight={"48px"}>Scanner</Text>
              </Flex>
            </ModalHeader>
            <ModalCloseButton onClick={onClose} />
            <ModalBody>
              <Grid gridTemplateColumns={"1fr 1fr"} gap="40px">
                <Card
                  height={"10rem"}
                  onClick={() =>
                    handleChangeState(ScanModalStatus.CHANGE_STATUS)
                  }
                  isActive={actionStatus === ScanModalStatus.CHANGE_STATUS}
                >
                  <Flex
                    direction={"column"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    gap="40px"
                    my="40px"
                  >
                    <CheckIcon color="brand.700" w="33px" h="33px"></CheckIcon>
                    <Text
                      color="gray.800"
                      fontWeight={"600"}
                      fontSize={"18px"}
                      lineHeight={"27px"}
                    >
                      Changement de status
                    </Text>
                  </Flex>
                </Card>
                <Card
                  height={"10rem"}
                  onClick={() => handleChangeState(ScanModalStatus.PREP_ORDER)}
                  isActive={actionStatus === ScanModalStatus.PREP_ORDER}
                >
                  <Flex
                    direction={"column"}
                    justifyContent={"center"}
                    alignItems={"center"}
                    gap="40px"
                    my="40px"
                  >
                    <BoxIcon color="brand.700" w="33px" h="33px"></BoxIcon>
                    <Text
                      color="gray.800"
                      fontWeight={"600"}
                      fontSize={"18px"}
                      lineHeight={"27px"}
                    >
                      Préparation du colis
                    </Text>
                  </Flex>
                </Card>
              </Grid>

              {actionStatus === ScanModalStatus.CHANGE_STATUS && (
                <ScanModalChangeStatusBody
                  defaultStatus={changeToStatus}
                  onUpdate={(status: CommandStatus | null) =>
                    setChangeToStatus(status)
                  }
                ></ScanModalChangeStatusBody>
              )}
              {actionStatus === ScanModalStatus.PREP_ORDER && (
                <ScanModalPrepOrderBody
                  defaultStatus={prepCommandShippingType}
                  onUpdate={(type: ShippingType | null) =>
                    setPrepCommandShippingType(type)
                  }
                ></ScanModalPrepOrderBody>
              )}
            </ModalBody>
            <ModalFooter borderTop="solid 1px #E2E8F0">
              <Grid gridTemplateColumns={"1fr 1fr"} gap="40px" w="100%">
                <Flex p={3} justifyContent="center" onClick={onClose}>
                  Annuler
                </Flex>
                <Flex
                  p={3}
                  justifyContent="center"
                  bg={canGoNext ? "brand.700" : "gray.200"}
                  color={canGoNext ? "white" : "gray.400"}
                  borderRadius={"xl"}
                  onClick={handleGoNext}
                >
                  Confirmer
                </Flex>
              </Grid>
            </ModalFooter>
          </ModalContent>
        </Modal>
        {changeToStatus != null && (
          <ChangeStatusModal
            status={changeToStatus}
            setStatus={setChangeToStatus}
            isOpen={changeStatusIsOpen}
            onOpen={changeStatusOnOpen}
            onClose={changeStatusOnClose}
            goBack={() => {
              changeStatusOnClose();
              onOpen();
            }}
          ></ChangeStatusModal>
        )}
        {prepCommandShippingType !== null && (
          <PrepCommandModal
            isOpen={prepCommandModal.isOpen}
            onClose={prepCommandModal.onClose}
            goBack={() => {
              prepCommandModal.onClose();
              onOpen();
            }}
            deliveryType={prepCommandShippingType}
          />
        )}
      </>
    );
  }
);

export default ScanModal;
